import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Table,
  Tooltip,
  notification,
} from "antd";
import React, { useState } from "react";
import LoadingOverlay from "react-loading-overlay";
import { useQueryClient } from "react-query";

import { QUERY_KEY } from "@Constants/query-keys";
import useGetQueueAllConfigurations from "@Hooks/queries/useGetQueueAllConfigurations";
import useUpdateQueueConfigurationById from "@Hooks/mutation/useUpdateQueueConfigurationById";

import "./Configuration.css";

const CONFIG_TYPE = {
  passed_queue_threshold: "number",
  passed_queue_notice: "textarea",
};

const Configuration = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();

  const { data: configurations, isLoading } = useGetQueueAllConfigurations();
  const updateConfigMutation = useUpdateQueueConfigurationById();

  const showModal = (record) => {
    setEditingConfig(record);

    form.setFieldsValue({
      value:
        CONFIG_TYPE[record.key] === "number"
          ? Number(record.value)
          : record.value,
      description: record.description,
    });

    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
    form.resetFields();
  };

  const onFinish = (values) => {
    let payload = {
      ...editingConfig,
      value: values.value,
      description: values.description,
      _method: "PUT",
    };

    // if (CONFIG_TYPE[editingConfig.key] === "number") {
    //     payload[editingConfig.key] = Number(values.value);
    // } else {
    //     payload[editingConfig.key] = values.value;
    // }

    updateConfigMutation.mutate(
      { id: editingConfig.id, payload },
      {
        onSuccess: () => {
          notification.success({
            message: "Berhasil",
            description: "Konfigurasi berhasil diperbarui",
          });
          queryClient.invalidateQueries([
            QUERY_KEY.GET_QUEUE_ALL_CONFIGURATION,
          ]);
          handleCancel();
        },
        onError: (error) => {
          notification.error({
            message: "Gagal",
            description:
              error?.response?.data?.message || "Gagal memperbarui konfigurasi",
          });
        },
      },
    );
  };

  const renderValueInput = () => {
    const type = CONFIG_TYPE[editingConfig?.key];

    if (type === "number") {
      return (
        <InputNumber
          style={{ width: "100%" }}
          placeholder="Masukkan nilai (angka)"
        />
      );
    }

    if (type === "textarea") {
      return <Input.TextArea placeholder="Masukkan teks" rows={4} />;
    }

    return <Input placeholder="Masukkan nilai konfigurasi" />;
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Key",
      dataIndex: "key",
      key: "key",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Nilai",
      dataIndex: "value",
      key: "value",
      render: (value, record) => {
        if (record.key === "passed_queue_notice") {
          return (
            <Tooltip title={value}>
              <div style={{ maxWidth: 400 }}>
                {value.length > 100 ? value.slice(0, 100) + "..." : value}
              </div>
            </Tooltip>
          );
        }
        return value;
      },
    },
    {
      title: "Deskripsi",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: 400 }}>
            {text?.length > 120 ? text.slice(0, 120) + "..." : text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Dibuat",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Diperbarui",
      dataIndex: "updated_at",
      key: "updated_at",
    },
    {
      title: "Aksi",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button type="primary" onClick={() => showModal(record)}>
          Ubah
        </Button>
      ),
    },
  ];

  return (
    <>
      
      <LoadingOverlay active={isLoading} spinner text="Memuat data...">
        <div className="content-section">
          <div className="title-section mb-5">
            <h1 className="patient-page-title">Konfigurasi Sistem</h1>
            <h2 className="patient-page-subtitle">
              <span style={{ color: "#039BEB" }}>Master Data</span>
              <span style={{ color: "#00000029" }}> / Konfigurasi</span>
            </h2>
          </div>

          <div className="card shadow mb-4">
            <div className="card-body">
              <Table
                dataSource={configurations?.queue_configurations || []}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </div>
          </div>
        </div>
      </LoadingOverlay>

      <Modal
        title={`Ubah Konfigurasi: ${editingConfig?.key || ""}`}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="value"
            label="Nilai"
            rules={[
              {
                required: true,
                message: "Nilai konfigurasi harus diisi",
              },
              ...(CONFIG_TYPE[editingConfig?.key] === "number"
                ? [
                    {
                      type: "number",
                      min: 0,
                      max: 50,
                      message: "Harus angka antara 0 - 50",
                    },
                  ]
                : []),
              ...(CONFIG_TYPE[editingConfig?.key] === "textarea"
                ? [
                    {
                      max: 500,
                      message: "Maksimal 500 karakter",
                    },
                  ]
                : []),
            ]}
          >
            {renderValueInput()}
          </Form.Item>

          {/* ✅ Field deskripsi */}
          <Form.Item
            name="description"
            label="Deskripsi"
            rules={[
              {
                max: 255,
                message: "Maksimal 255 karakter",
              },
            ]}
          >
            <Input.TextArea
              placeholder="Masukkan deskripsi konfigurasi"
              rows={4}
              showCount
              maxLength={255}
            />
          </Form.Item>

          <div style={{ textAlign: "right" }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              Batal
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateConfigMutation.isLoading}
            >
              Simpan
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Configuration;
